import type { BrandPalette } from '../types';

/**
 * DEFAULT_PALETTE — Figma defaults currently encoded in `index.css` and
 * `pages/DesignSystem/sections/TokensSection.tsx`. This is the fallback
 * chain's terminal value (Layer 1 default before any partner override).
 */
export const DEFAULT_PALETTE: Required<BrandPalette> = {
  primary: '#22E11C',
  secondary: '#1A2B4C',
  accent: '#FF3DBE',
  neutral: '#878B95',
  success: '#16A34A',
  danger: '#DC2626',
  surfaceBg: '#FFFFFF',
  surfaceCard: '#F2F2F2',
  surfaceBorder: '#EAEAEB',
  textPrimary: '#15181E',
  textMuted: '#878B95',
  textOnBrand: '#FFFFFF',
  heroFrom: '#2A4FA8',
  heroTo: '#B62036',
  // Modern-template page background gradient. Values mirror the
  // `:root[data-template='modern']` block in `src/index.css` (lines 320-321).
  pageBgFrom: '#1A0B0B',
  pageBgTo: '#000000',
};

/**
 * Lookup a palette role. Returns the override value if present, otherwise
 * the default value from DEFAULT_PALETTE. Never returns `undefined` — downstream
 * code can treat result as a non-empty string.
 */
export function paletteRole(
  p: BrandPalette | undefined,
  role: keyof BrandPalette,
): string {
  const override = p?.[role];
  if (override) return override;
  return DEFAULT_PALETTE[role];
}

/**
 * Merge a partial palette override over a base. `undefined` keys in the
 * override are skipped (the base value stays). Used when reading the v2
 * payload from backend — backend sends only roles partner customised.
 */
export function mergePalette(
  base: BrandPalette,
  override: BrandPalette | undefined,
): BrandPalette {
  if (!override) return { ...base };
  const out: BrandPalette = { ...base };
  for (const key of Object.keys(override) as (keyof BrandPalette)[]) {
    const v = override[key];
    if (v !== undefined) out[key] = v;
  }
  return out;
}
